import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { Button } from '@leafwell/components';
import { useDraggableScroll } from 'hooks/useDraggableScroll';
import Image from 'next/image';

const AlphabetList: React.FC<{
  initialLetter?: string;
  letterNumbers?: string[];
  showNumbersFilter?: boolean;
  useHash?: boolean;
  disableUrlChange?: boolean;
  onChangeLetter?: (letter: string) => void;
  loading?: boolean;
  bgColor?: string;
}> = ({
  useHash,
  letterNumbers = [],
  disableUrlChange,
  initialLetter,
  showNumbersFilter,
  onChangeLetter,
  loading,
  bgColor = 'white',
}) => {
  const [showStartButton, setShowStartButton] = useState(false);
  const [showEndButton, setShowEndButton] = useState(true);
  const { asPath, locale, query } = useRouter();

  const numbers = letterNumbers?.length ? letterNumbers.join('') : '123456789';
  const alphabet = [
    showNumbersFilter ? numbers : '',
    'abcdefghijklmnopqrstuvwxyz',
  ]
    .join('')
    .split('');
  const dragEl = useRef(null);
  const currentLetterRef = useRef(null);
  const [activeLetter, setActiveLetter] = useState<string>();
  const { onMouseDown, updateScroll } = useDraggableScroll({
    ref: dragEl,
    direction: 'horizontal',
  });
  const path = asPath.replace(query?.slug as string, '').split('?')[0];
  const pathname = locale === 'en' ? path : `/${locale}${path}`;

  useEffect(() => {
    if (asPath.split('#')[1] || initialLetter?.toLowerCase()) {
      setActiveLetter(asPath.split('#')[1] || initialLetter?.toLowerCase());
    }
  }, [asPath, initialLetter, setActiveLetter]);

  const watchScroll = () => {
    const firstListItem = dragEl.current.querySelector(
      showNumbersFilter ? "[id='letter-1']" : "[id='letter-a']",
    );
    const lastListItem = dragEl.current.querySelector("[id='letter-z']");

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.target === firstListItem) {
            setShowStartButton(!entry.isIntersecting);
          }

          if (entry.target === lastListItem) {
            setShowEndButton(!entry.isIntersecting);
          }
        });
      },
      { threshold: 0.5 },
    );

    observer.observe(firstListItem);
    observer.observe(lastListItem);
  };

  useEffect(() => {
    watchScroll();
  }, [dragEl.current]);

  const scrollLeft = () => {
    if (dragEl.current) {
      dragEl.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (dragEl.current) {
      dragEl.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (currentLetterRef?.current) {
      updateScroll(
        currentLetterRef.current.offsetLeft -
          window.innerWidth / 2 +
          currentLetterRef.current.offsetWidth / 2,
      );
    }
  }, [currentLetterRef]);

  return (
    <div className={`bg-${bgColor} overflow-hidden`}>
      <ul
        className={
          'grid grid-flow-col gap-x-2 lg:gap-x-1 overflow-x-auto hide-scrollbar pt-3 pb-3'
        }
        ref={dragEl}
        onMouseDown={onMouseDown}
      >
        {alphabet.map(letter => {
          const isActive = activeLetter === letter;
          const obj = { id: `letter-${letter}` };
          const additionalParams = disableUrlChange
            ? obj
            : useHash
            ? {
                ...obj,
                href: `#${letter}`,
              }
            : {
                ...obj,
                href: `${pathname}#${letter}`,
              };

          return (
            <li
              {...{
                key: letter,
                ...(isActive ? { ref: currentLetterRef } : {}),
              }}
              className="z-0"
            >
              <Button
                label={letter}
                loading={loading && isActive}
                size="small"
                variant={isActive ? 'primary' : 'secondary'}
                filled={false}
                className={[
                  'p-reset inline-grid items-center w-8 h-8 uppercase text-sm font-sans font-medium rounded-full',
                  isActive
                    ? 'bg-green-100 pointer-events-none'
                    : '!border-indigo-200',
                ].join(' ')}
                onClick={() => {
                  setActiveLetter(letter);
                  if (onChangeLetter) onChangeLetter(letter);
                }}
                {...additionalParams}
              />
            </li>
          );
        })}
      </ul>
      <div className="flex -ml-5">
        <button
          disabled={!showStartButton}
          className="flex justify-center items-center w-14 h-[30px] transition-opacity disabled:opacity-50"
          onClick={scrollLeft}
        >
          <Image
            width={15}
            height={12}
            src="/arrow.svg"
            alt="left control arrow"
            className="justify-self-end rotate-180"
          />
        </button>
        <button
          disabled={!showEndButton}
          className="flex justify-center items-center w-14 h-[30px] transition-opacity disabled:opacity-50"
          onClick={scrollRight}
        >
          <Image
            width={15}
            height={12}
            src="/arrow.svg"
            alt="right control arrow"
            className="justify-self-end"
          />
        </button>
      </div>
    </div>
  );
};

export default AlphabetList;
